/**
 * Label Constants
 *
 * Centralized configuration for label dimensions and styling.
 * These values are derived from QR-Spec.md and thermal printer requirements.
 */

/**
 * Label physical dimensions for 4x3" thermal labels.
 * Used by both GarmentLabel component and print CSS.
 */
export const LABEL_DIMENSIONS = {
  /** Width in inches (matches Zebra thermal label stock) */
  WIDTH: '4in',
  /** Height in inches (matches Zebra thermal label stock) */
  HEIGHT: '3in',
  /** Padding inside label border */
  PADDING_PX: 12,
  /** QR column width for grid layout */
  QR_COLUMN_WIDTH: '140px',
} as const;

/**
 * QR code configuration.
 * Size optimized for reliable scanning at arm's length.
 */
export const QR_CONFIG = {
  /** QR code size in pixels (120px = ~1.2" at 100dpi, scannable) */
  SIZE_PX: 120,
  /**
   * Error correction level: M (15% recovery)
   * Upgraded from 'L' (7%) for better scanning reliability in
   * production environments where labels may get dirty or damaged.
   * Trade-off: Slightly denser QR code, but still scannable at our size.
   */
  ERROR_CORRECTION: 'M' as const,
} as const;

/**
 * Print configuration for different formats.
 */
export const PRINT_CONFIG = {
  /** Thermal label print settings */
  LABEL: {
    WIDTH: LABEL_DIMENSIONS.WIDTH,
    HEIGHT: LABEL_DIMENSIONS.HEIGHT,
  },
  /** Picklist print settings (A4 paper) */
  PICKLIST: {
    SIZE: 'A4',
    MARGIN: '20mm',
  },
  /** Cleanup timeout after print dialog (ms) */
  CLEANUP_TIMEOUT_MS: 2000,
} as const;

/**
 * Compact label dimensions for 57x32mm thermal labels.
 * Used for HeapsGood 32x57mm label stock (rotated orientation).
 */
export const COMPACT_LABEL_DIMENSIONS = {
  /** Width in mm (matches HeapsGood thermal label stock) */
  WIDTH: '57mm',
  /** Height in mm (matches HeapsGood thermal label stock) */
  HEIGHT: '32mm',
  /** Padding inside label border */
  PADDING_PX: 4,
  /** QR column width for grid layout (~18mm for scannable QR) */
  QR_COLUMN_WIDTH: '72px',
} as const;

/**
 * Compact QR code configuration.
 * Minimum scannable size for arm's length scanning.
 */
export const COMPACT_QR_CONFIG = {
  /** QR code size in pixels (68px = ~18mm, minimum for reliable scanning) */
  SIZE_PX: 68,
  /** Error correction level: M (15% recovery) for production environment */
  ERROR_CORRECTION: 'M' as const,
} as const;

/**
 * Colors used in label rendering.
 */
export const LABEL_COLORS = {
  BACKGROUND: '#ffffff',
  BORDER: '#000000',
  ERROR_BACKGROUND: '#fef2f2',
  ERROR_BORDER: '#dc2626',
  ERROR_TEXT: '#dc2626',
  REDO_BADGE: '#dc2626',
  PRETREAT_BADGE_BG: '#000000',
  PRETREAT_BADGE_TEXT: '#ffffff',
  // Phase 50.01.3 / BLU-233: DTF print-method indicator. A distinct semantic
  // colour (indigo) so a DTF garment never reads as a DTG black box on the
  // shop floor. DTG keeps the BORDER (black) box.
  DTF_INDICATOR_BG: '#4338ca',
  DTF_INDICATOR_TEXT: '#ffffff',
} as const;
